import './device-health.scss';
import {Avatar, Box, CircularProgress, Typography, useTheme} from '@mui/material';
import {ComposableMap, Geographies, Geography, Annotation} from 'react-simple-maps';
import {useContext, useEffect, useState} from 'react';
import {HealthCheck} from '../../backend/interfaces/health-check';
import {DevicesRepository} from '../../repositories/devices';
import {ContentPage} from '../content-page/content-page';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {avatarSizesAndCoefficients} from '../../utils/constants/avatar-sizes-and-coefficients';
import {User} from '../../backend/interfaces/user';
import {Entity} from '../../backend/interfaces/entity';
import {UsersRepository} from '../../repositories/users';
import {EntitiesRepository} from '../../repositories/entities';
import {CaregiverMode} from '../../enums/caregiver-mode';
import {BackendLanguageTag} from '../../backend/enums/backend-language-tag';
import {CountryType} from '../../interfaces/country-type';
import {countries} from '../../i18n/countries';
import {AppLanguageTag} from '../../i18n/types';
import {DataCacheManager} from '../../managers/data-cache';
import {ApplicationsRepository} from '../../repositories/applications';
import {AuxiliaryDataCacheContextType} from '../../contexts/auxiliary-data-cache/types';
import {AuxiliaryDataCacheContext} from '../../contexts/auxiliary-data-cache/context';
import {LoadingContent} from '../loading-content/loading-content';
import {isSvg} from '../../utils/functions/validate-uri-svg';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import moment from 'moment';
import i18next from 'i18next';

// Section title
const SectionTitle = (props: {title: string}) => (
  <Box className="device-health-section-title-container">
    <Typography variant="h6" component="span">
      {props.title}
    </Typography>
  </Box>
);

// Section subtitle
const SectionSubtitle = (props: {subtitle: string}) => (
  <Box className="device-health-section-subtitle-container">
    <Typography variant="subtitle1" component="span" color="text.secondary">
      {props.subtitle}
    </Typography>
  </Box>
);

// Info box
const InfoBox = (props: {
  label: string;
  avatar?: {
    type: 'user' | 'entity' | 'other';
    isIcon?: boolean;
    url?: string;
  };
  value?: string;
}) => {
  // Get theme
  const theme = useTheme();

  // Render avatar
  const renderAvatar = (
    type: 'user' | 'entity' | 'other',
    isIcon: boolean,
    url?: string,
    alt?: string
  ) => {
    // Avatar sizes, coefficients and default icon
    const avatarSize: number = avatarSizesAndCoefficients.avatarSizes.large;
    const iconSize: number = avatarSize * avatarSizesAndCoefficients.iconSizeCoefficient;
    let defaultAvatarIcon: React.ReactElement = <></>;
    let avatarBorderRadius: string | undefined;
    switch (type) {
      case 'user':
        defaultAvatarIcon = <PersonIcon sx={{fontSize: iconSize}} />;
        avatarBorderRadius = `${avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.circle}px`;
        break;
      case 'entity':
        defaultAvatarIcon = <BusinessIcon sx={{fontSize: iconSize}} />;
        avatarBorderRadius = `${
          avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersLow
        }px`;
        break;
      default:
        defaultAvatarIcon = <></>;
        avatarBorderRadius = `${
          avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersHigh
        }px`;
        break;
    }

    return isIcon ? (
      <Avatar
        className="device-health-avatar-container"
        sx={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: avatarBorderRadius,
          borderColor: theme.palette.action.disabled,
        }}
      >
        {url ? (
          isSvg(url) ? (
            <object
              data={url}
              aria-label={alt}
              type="image/svg+xml"
              width={`${iconSize}`}
              height={`${iconSize}`}
            ></object>
          ) : (
            <img
              style={{
                width: `${iconSize}px`,
                height: `${iconSize}px`,
              }}
              src={url}
              alt={alt}
            />
          )
        ) : (
          defaultAvatarIcon
        )}
      </Avatar>
    ) : (
      <Avatar
        className="device-health-avatar-container"
        src={url}
        alt={alt}
        sx={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: avatarBorderRadius,
          borderColor: theme.palette.action.disabled,
        }}
      >
        {defaultAvatarIcon}
      </Avatar>
    );
  };

  return (
    <Box className="device-health-info-box-container">
      <Typography
        variant="subtitle2"
        color="text.secondary"
        className="device-health-info-box-label"
      >
        {props.label}
      </Typography>
      <Box className="device-health-info-box-value-container">
        {props.value ? (
          <>
            {props.avatar && (
              <Box className="device-health-info-box-value-avatar">
                {renderAvatar(
                  props.avatar.type,
                  props.avatar.isIcon === true ? true : false,
                  props.avatar.url,
                  props.value
                )}
              </Box>
            )}
            <Typography variant="body1">{props.value}</Typography>
          </>
        ) : (
          <Typography variant="body1">-</Typography>
        )}
      </Box>
    </Box>
  );
};

// Circular progress info box
const CircularProgressInfoBox = (props: {label: string; value?: number}) => (
  <Box className="device-health-info-box-container">
    <Typography variant="subtitle2" color="text.secondary" className="device-health-info-box-label">
      {props.label}
    </Typography>
    {props.value ? (
      <Box className="device-health-info-box-circular-progress-value-container">
        <CircularProgress variant="determinate" value={props.value} />
        <Box className="device-health-info-box-circular-progress-value-text-container">
          <Typography variant="caption" component="div">{`${Math.round(props.value)}%`}</Typography>
        </Box>
      </Box>
    ) : (
      <Box className="device-health-info-box-value-container">
        <Typography variant="body1">-</Typography>
      </Box>
    )}
  </Box>
);

//

/**
 * Device health information.
 *
 * This form was created taking into account that it will exist under the devices tabs.
 * Later on, if it's necessary, we can extend this component to be applied to more contexts.
 */
export const DeviceHealth = (props: {
  deviceId: string;
  caregiverMode: CaregiverMode.Professional | CaregiverMode.SystemAdministrator;
  canWrite: boolean;
}) => {
  // Get context
  const auxiliaryDataCacheContext: AuxiliaryDataCacheContextType =
    useContext(AuxiliaryDataCacheContext);

  // Hold state
  const [isLoadingDeviceHealth, setIsLoadingDeviceHealth] = useState<boolean>(true);
  const [deviceHealth, setDeviceHealth] = useState<HealthCheck | undefined>();
  const [deviceUser, setDeviceUser] = useState<User | undefined>();
  const [deviceEntity, setDeviceEntity] = useState<Entity | undefined>();
  const [processedTrackingData, setProcessedTrackingData] = useState<
    | {
        country?: string;
        countryCode?: string;
        city?: string;
        cityLongitude?: number;
        cityLatitude?: number;
        userIP?: string;
        languageCode?: BackendLanguageTag;
        language?: string;
        region?: string;
      }
    | undefined
  >();
  const [isLoadingProcessedApplications, setIsLoadingProcessedApplications] =
    useState<boolean>(true);
  const [processedApplications, setProcessedApplications] = useState<
    Array<{
      applicationName: string;
      applicationVersion?: string;
      applicationPictureUrl?: string;
    }>
  >();
  const [processedVersions, setProcessedVersions] = useState<
    | {
        agent: string;
        chrome: string;
        electron: string;
        launcherBinary: string;
        launcherFrontend: string;
      }
    | undefined
  >();

  // Translations
  const {t} = useTranslation();

  // Get theme
  const theme = useTheme();

  // Get the device health info
  useEffect(() => {
    const getDeviceHealth = async () => {
      try {
        // Start loading
        setIsLoadingDeviceHealth(true);
        setIsLoadingProcessedApplications(true);

        // Reset values
        setDeviceHealth(undefined);
        setDeviceEntity(undefined);
        setDeviceUser(undefined);
        setProcessedTrackingData(undefined);
        setProcessedApplications(undefined);

        // Fetch from backend
        const result = await DevicesRepository.instance.getHealthById(props.deviceId);

        if (result) {
          // Set health
          setDeviceHealth(result);
        } else {
          // Error
          throw new Error('Empty device health.');
        }

        // Get the user
        if (result.userId) {
          try {
            // Fetch from backend
            const userResult = await UsersRepository.instance.getById(result.userId);

            if (userResult) {
              // Set user
              setDeviceUser(userResult);
            } else {
              // Error
              throw new Error('Empty device user.');
            }
          } catch (error) {
            // Log
            console.debug(`🔴 Could not get the device user with id ${result.userId}.`, error);
          }
        }

        // Get the entity
        if (result.entityId) {
          try {
            // Fetch from backend
            const entityResult = await EntitiesRepository.instance.getById(result.entityId);

            if (entityResult) {
              // Set entity
              setDeviceEntity(entityResult);
            } else {
              // Error
              throw new Error('Empty device entity.');
            }
          } catch (error) {
            // Log
            console.debug(`🔴 Could not get the device entity with id ${result.entityId}.`, error);
          }
        }

        // Process the request tracking data
        if (result.requestTracking) {
          // Find country
          const country: CountryType | undefined = countries.find(
            (obj) =>
              obj.code.toLocaleLowerCase() === result.requestTracking?.country?.toLocaleLowerCase()
          );

          // Set processed tracking
          setProcessedTrackingData({
            countryCode: result.requestTracking.country,
            cityLatitude: result.requestTracking.cityLatitude,
            cityLongitude: result.requestTracking.cityLongitude,
            languageCode: result.requestTracking.languageCode,
            language: result.requestTracking.languageCode
              ? t(translations.languages[result.requestTracking.languageCode])
              : undefined,
            userIP: result.requestTracking.userIP,
            country: country?.label[i18next.language as AppLanguageTag] || undefined,
            city: result.requestTracking.city
              ? result.requestTracking.city.charAt(0).toUpperCase() +
                result.requestTracking.city.slice(1)
              : undefined,
            region: result.requestTracking.region,
          });
        }

        // Process applications
        processApplications(result.applications);

        setProcessedVersions(result.versions);
      } catch (error) {
        // Log
        console.debug(
          `🔴 Could not get the device health for device with id ${props.deviceId}.`,
          error
        );
      } finally {
        // Stop loading
        setIsLoadingDeviceHealth(false);
      }
    };

    // Get device health
    getDeviceHealth();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.deviceId]);

  // Process applications
  const processApplications = async (healthCheckApplications: HealthCheck['applications']) => {
    // Check
    if (
      !healthCheckApplications ||
      (healthCheckApplications && healthCheckApplications.length === 0)
    ) {
      // Set processed applications
      setProcessedApplications(undefined);

      // Stop loading
      setIsLoadingProcessedApplications(false);

      // Stop
      return;
    }

    // Start loading
    setIsLoadingProcessedApplications(true);

    // Applications
    let applications: Array<{
      applicationName: string;
      applicationVersion?: string;
      applicationPictureUrl?: string;
    }> = [];

    // Get applications
    for (let i = 0; i < healthCheckApplications.length; i++) {
      if (healthCheckApplications[i].applicationId) {
        // Check inside cache first to reduce the number of requests
        if (DataCacheManager.instance.applications[`${healthCheckApplications[i].applicationId}`]) {
          // Set the application
          applications.push({
            applicationName:
              DataCacheManager.instance.applications[`${healthCheckApplications[i].applicationId}`]
                .name,
            applicationPictureUrl:
              DataCacheManager.instance.applications[`${healthCheckApplications[i].applicationId}`]
                .picture,
            applicationVersion: healthCheckApplications[i].applicationVersion,
          });
        } else {
          // Fetch from backend
          try {
            // Get application
            const applicationResult = await ApplicationsRepository.instance.getById(
              healthCheckApplications[i].applicationId as string,
              true
            );

            if (applicationResult) {
              // Set the application
              applications.push({
                applicationName: applicationResult.name,
                applicationPictureUrl: applicationResult.picture,
                applicationVersion: healthCheckApplications[i].applicationVersion,
              });

              // Save it on cache
              DataCacheManager.instance.addApplication(
                healthCheckApplications[i].applicationId as string,
                applicationResult
              );

              // Save it on context
              auxiliaryDataCacheContext.setApplications(DataCacheManager.instance.applications);
            } else {
              // Error
              throw new Error('Empty application.');
            }
          } catch (error) {
            // Log
            console.debug(
              `🔴 Could not get the application with id ${healthCheckApplications[i].applicationId}.`,
              error
            );
          }
        }
      }
    }

    // Set processed applications
    setProcessedApplications(applications);

    // Stop loading
    setIsLoadingProcessedApplications(false);
  };

  const signalStrength = deviceHealth?.mobileData?.signalStrengthDescription?.replace(
    '-signal',
    ''
  );

  return (
    <>
      {isLoadingDeviceHealth ? (
        <LoadingContent />
      ) : (
        <ContentPage>
          {deviceHealth ? (
            <Box className="device-health-container">
              {/* ----- Last recording date and time ----- */}
              <Box className="device-health-last-recording-container">
                <Box className="device-health-last-recording-live" />
                <Typography
                  variant="subtitle2"
                  component="p"
                  className="device-health-last-recording-label"
                >
                  {t(translations.device_health.last_recording)}:
                </Typography>
                <Typography
                  variant="subtitle2"
                  component="p"
                  className="device-health-last-recording-date-time"
                >
                  {moment(deviceHealth.registeredAt).format('LLL')}
                </Typography>
              </Box>
              {/* ----- Basic information ----- */}
              <SectionTitle title={t(translations.device_health.basic_information_section_title)} />
              <Box className="device-health-section-container">
                <InfoBox
                  label={t(translations.device_health.user_label)}
                  value={deviceUser?.name}
                  avatar={{
                    type: 'user',
                    url: deviceUser?.picture,
                  }}
                />
                {props.caregiverMode === CaregiverMode.SystemAdministrator && (
                  <InfoBox
                    label={t(translations.device_health.entity_label)}
                    value={deviceEntity?.name}
                    avatar={{
                      type: 'entity',
                      url: deviceEntity?.picture,
                    }}
                  />
                )}
              </Box>
              {/* ----- Wi-Fi ----- */}
              <SectionTitle title={t(translations.device_health.wifi_section_title)} />
              <Box className="device-health-section-container">
                <InfoBox
                  label={t(translations.device_health.data_usage_label)}
                  value={
                    deviceHealth.wifi?.dataUsage
                      ? `${(deviceHealth.wifi.dataUsage / (1024 * 1024)).toFixed(2)} ${t(
                          translations.device_health.mb_unit_label
                        )}`
                      : undefined
                  }
                />
              </Box>
              <Box className="device-health-subsection-container">
                <SectionSubtitle
                  subtitle={t(translations.device_health.networks_wifi_subsection_title)}
                />
                <Box className="device-health-subsection-values-container">
                  {deviceHealth.wifi?.networks && deviceHealth.wifi.networks.length > 0 ? (
                    deviceHealth.wifi.networks.map((network, index) => {
                      return (
                        <Box
                          key={`health-device-wifi-networks-${index}`}
                          className="device-health-subsection-values-row-container"
                        >
                          <InfoBox
                            label={t(translations.device_health.ssid_label)}
                            value={network.ssid}
                          />
                          <CircularProgressInfoBox
                            label={t(translations.device_health.quality_label)}
                            value={network.quality}
                          />
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body1" component="div">
                      {t(translations.device_health.no_data)}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box className="device-health-subsection-container">
                <SectionSubtitle
                  subtitle={t(translations.device_health.current_wifi_subsection_title)}
                />
                <Box className="device-health-subsection-values-container">
                  {deviceHealth.wifi?.current && deviceHealth.wifi.current.length > 0 ? (
                    deviceHealth.wifi.current.map((network, index) => {
                      return (
                        <Box
                          key={`health-device-wifi-current-${index}`}
                          className="device-health-subsection-values-row-container"
                        >
                          <InfoBox
                            label={t(translations.device_health.ssid_label)}
                            value={network.ssid}
                          />
                          <CircularProgressInfoBox
                            label={t(translations.device_health.quality_label)}
                            value={network.quality}
                          />
                          <InfoBox
                            label={t(translations.device_health.ip_address_label)}
                            value={network.ipAddress}
                          />
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body1" component="div">
                      {t(translations.device_health.no_data)}
                    </Typography>
                  )}
                </Box>
              </Box>
              {/* ----- Mobile data ----- */}
              <SectionTitle title={t(translations.device_health.mobile_data_section_title)} />
              <Box className="device-health-section-container">
                <InfoBox
                  label={t(translations.device_health.is_data_usage_enabled_label)}
                  value={
                    deviceHealth.mobileData?.isDataUsageEnabled === true
                      ? t(translations.device_health.yes_label)
                      : deviceHealth.mobileData?.isDataUsageEnabled === false
                        ? t(translations.device_health.no_label)
                        : undefined
                  }
                />
                <InfoBox
                  label={t(translations.device_health.ip_address_label)}
                  value={deviceHealth.mobileData?.ipAddress}
                />
                <InfoBox
                  label={t(translations.device_health.carrier_label)}
                  value={deviceHealth.mobileData?.carrier}
                />
                <CircularProgressInfoBox
                  label={t(translations.device_health.quality_label)}
                  value={deviceHealth.mobileData?.quality}
                />
                <InfoBox
                  label={t(translations.device_health.network_type_label)}
                  value={
                    deviceHealth.mobileData?.networkType
                      ? t(translations.mobile_network_types[deviceHealth.mobileData.networkType])
                      : undefined
                  }
                />
                <InfoBox
                  label={t(translations.device_health.data_usage_label)}
                  value={
                    deviceHealth.mobileData?.dataUsage
                      ? `${deviceHealth.mobileData.dataUsage}${t(
                          translations.device_health.mb_unit_label
                        )}`
                      : undefined
                  }
                />
                <InfoBox
                  label={t(translations.device_health.signal_strength_label)}
                  value={
                    signalStrength === 'no' ||
                    signalStrength === 'weak' ||
                    signalStrength === 'fair' ||
                    signalStrength === 'good' ||
                    signalStrength === 'strong'
                      ? // @ts-ignore
                        t(translations.device_health['signal_strength_' + signalStrength])
                      : undefined
                  }
                />
                <InfoBox
                  label={t(translations.device_health.signal_strength_level_label)}
                  value={deviceHealth.mobileData?.signalStrengthLevel?.toString() || undefined}
                />
                <InfoBox
                  label={t(translations.device_health.dialer_label)}
                  value={deviceHealth.mobileData?.phoneDialerPackageName || undefined}
                />
              </Box>
              {/* ----- Graphics ----- */}
              <SectionTitle title={t(translations.device_health.graphics_section_title)} />
              <Box className="device-health-subsection-container">
                <SectionSubtitle
                  subtitle={t(translations.device_health.controllers_graphics_subsection_title)}
                />
                <Box className="device-health-subsection-values-container">
                  {deviceHealth.graphics?.controllers &&
                  deviceHealth.graphics.controllers.length > 0 ? (
                    deviceHealth.graphics.controllers.map((controller, index) => {
                      return (
                        <Box
                          key={`health-device-graphics-controllers-${index}`}
                          className="device-health-subsection-values-row-container"
                        >
                          <InfoBox
                            label={t(translations.device_health.brand_label)}
                            value={controller.brand}
                          />
                          <InfoBox
                            label={t(translations.device_health.model_label)}
                            value={controller.model}
                          />
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body1" component="div">
                      {t(translations.device_health.no_data)}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box className="device-health-subsection-container">
                <SectionSubtitle
                  subtitle={t(translations.device_health.displays_graphics_subsection_title)}
                />
                <Box className="device-health-subsection-values-container">
                  {deviceHealth.graphics?.displays && deviceHealth.graphics.displays.length > 0 ? (
                    deviceHealth.graphics.displays.map((display, index) => {
                      return (
                        <Box
                          key={`health-device-displays-controllers-${index}`}
                          className="device-health-subsection-values-row-container"
                        >
                          <InfoBox
                            label={t(translations.device_health.is_main_display_label)}
                            value={
                              (display.main ?? display.isMain) === true
                                ? t(translations.device_health.yes_label)
                                : (display.main ?? display.isMain) === false
                                  ? t(translations.device_health.no_label)
                                  : undefined
                            }
                          />
                          <InfoBox
                            label={t(translations.device_health.model_label)}
                            value={display.brand}
                          />
                          <InfoBox
                            label={t(translations.device_health.width_label)}
                            value={
                              display.size?.width
                                ? `${display.size.width}${t(
                                    translations.device_health.px_unit_label
                                  )}`
                                : undefined
                            }
                          />
                          <InfoBox
                            label={t(translations.device_health.height_label)}
                            value={
                              display.size?.height
                                ? `${display.size.height}${t(
                                    translations.device_health.px_unit_label
                                  )}`
                                : undefined
                            }
                          />
                          <InfoBox
                            label={t(translations.device_health.current_width_label)}
                            value={
                              display.currSize?.width
                                ? `${display.currSize.width}${t(
                                    translations.device_health.px_unit_label
                                  )}`
                                : undefined
                            }
                          />
                          <InfoBox
                            label={t(translations.device_health.current_height_label)}
                            value={
                              display.currSize?.height
                                ? `${display.currSize.height}${t(
                                    translations.device_health.px_unit_label
                                  )}`
                                : undefined
                            }
                          />
                          <InfoBox
                            label={t(translations.device_health.current_scale_factor_label)}
                            value={display.currSize?.scaleFactor?.toString()}
                          />
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body1" component="div">
                      {t(translations.device_health.no_data)}
                    </Typography>
                  )}
                </Box>
              </Box>
              {/* ----- System ----- */}
              <SectionTitle title={t(translations.device_health.system_section_title)} />
              <Box className="device-health-section-container">
                <InfoBox
                  label={t(translations.device_health.sku_label)}
                  value={deviceHealth.system?.sku}
                />
                <InfoBox
                  label={t(translations.device_health.serial_number_label)}
                  value={deviceHealth.system?.serialNumber}
                />
                <InfoBox
                  label={t(translations.device_health.brand_label)}
                  value={deviceHealth.system?.brand}
                />
                <InfoBox
                  label={t(translations.device_health.model_label)}
                  value={deviceHealth.system?.model}
                />
                <InfoBox
                  label={t(translations.device_health.version_label)}
                  value={deviceHealth.system?.version}
                />
              </Box>
              {/* ----- Battery ----- */}
              <SectionTitle title={t(translations.device_health.battery_section_title)} />
              <Box className="device-health-section-container">
                <InfoBox
                  label={t(translations.device_health.has_battery_label)}
                  value={
                    deviceHealth.battery?.hasBattery === true
                      ? t(translations.device_health.yes_label)
                      : deviceHealth.battery?.hasBattery === false
                        ? t(translations.device_health.no_label)
                        : undefined
                  }
                />
                <InfoBox
                  label={t(translations.device_health.is_battery_charging_label)}
                  value={
                    deviceHealth.battery?.isCharging === true
                      ? t(translations.device_health.yes_label)
                      : deviceHealth.battery?.isCharging === false
                        ? t(translations.device_health.no_label)
                        : undefined
                  }
                />
                <InfoBox
                  label={t(translations.device_health.is_battery_connected_to_power_label)}
                  value={
                    deviceHealth.battery?.isConnectedToPower === true
                      ? t(translations.device_health.yes_label)
                      : deviceHealth.battery?.isConnectedToPower === false
                        ? t(translations.device_health.no_label)
                        : undefined
                  }
                />
                <CircularProgressInfoBox
                  label={t(translations.device_health.battery_level_label)}
                  value={deviceHealth.battery?.level || undefined}
                />
              </Box>
              {/* ----- Disks ----- */}
              <SectionTitle title={t(translations.device_health.disks_section_title)} />
              <Box className="device-health-subsection-container">
                <Box className="device-health-subsection-values-container">
                  {deviceHealth.disks && deviceHealth.disks.length > 0 ? (
                    deviceHealth.disks.map((disk, index) => {
                      return (
                        <Box
                          key={`health-device-disks-${index}`}
                          className="device-health-subsection-values-row-container"
                        >
                          <InfoBox
                            label={t(translations.device_health.size_label)}
                            value={
                              disk.size
                                ? `${disk.size}${t(translations.device_health.gb_unit_label)}`
                                : undefined
                            }
                          />
                          <InfoBox
                            label={t(translations.device_health.used_disks_label)}
                            value={
                              disk.used
                                ? `${disk.used}${t(translations.device_health.gb_unit_label)}`
                                : undefined
                            }
                          />
                          <InfoBox
                            label={t(translations.device_health.available_label)}
                            value={
                              disk.available
                                ? `${disk.available}${t(translations.device_health.gb_unit_label)}`
                                : undefined
                            }
                          />
                          <InfoBox
                            label={t(translations.device_health.mount_label)}
                            value={disk.mount}
                          />
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body1" component="div">
                      {t(translations.device_health.no_data)}
                    </Typography>
                  )}
                </Box>
              </Box>
              {/* ----- Operating system ----- */}
              <SectionTitle title={t(translations.device_health.operating_system_section_title)} />
              <Box className="device-health-section-container">
                <InfoBox
                  label={t(translations.device_health.name_label)}
                  value={
                    deviceHealth.os?.name
                      ? t(translations.operative_system_names[deviceHealth.os.name])
                      : undefined
                  }
                />
                <InfoBox
                  label={t(translations.device_health.distribution_label)}
                  value={deviceHealth.os?.distribution}
                />
                <InfoBox
                  label={t(translations.device_health.release_label)}
                  value={deviceHealth.os?.release}
                />
                <InfoBox
                  label={t(translations.device_health.build_label)}
                  value={deviceHealth.os?.build}
                />
                <InfoBox
                  label={t(translations.device_health.web_view_version_label)}
                  value={deviceHealth.os?.webViewVersion}
                />
              </Box>
              {/* ----- CPU ----- */}
              <SectionTitle title={t(translations.device_health.cpu_section_title)} />
              <Box className="device-health-section-container">
                <InfoBox
                  label={t(translations.device_health.cores_label)}
                  value={deviceHealth.cpu?.cores?.toString()}
                />
                <InfoBox
                  label={t(translations.device_health.brand_label)}
                  value={deviceHealth.cpu?.brand}
                />
                <InfoBox
                  label={t(translations.device_health.model_label)}
                  value={deviceHealth.cpu?.model}
                />
              </Box>
              {/* ----- Memory ----- */}
              <SectionTitle title={t(translations.device_health.memory_section_title)} />
              <Box className="device-health-section-container">
                <InfoBox
                  label={t(translations.device_health.size_label)}
                  value={
                    deviceHealth.memory?.size
                      ? `${deviceHealth.memory.size.toString()}${t(
                          translations.device_health.gb_unit_label
                        )}`
                      : undefined
                  }
                />
                <InfoBox
                  label={t(translations.device_health.used_memory_label)}
                  value={
                    deviceHealth.memory?.used
                      ? `${deviceHealth.memory.used.toString()}${t(
                          translations.device_health.gb_unit_label
                        )}`
                      : undefined
                  }
                />
                <InfoBox
                  label={t(translations.device_health.available_label)}
                  value={
                    deviceHealth.memory?.available
                      ? `${deviceHealth.memory.available.toString()}${t(
                          translations.device_health.gb_unit_label
                        )}`
                      : undefined
                  }
                />
              </Box>
              {/* ----- Applications ----- */}
              <SectionTitle title={t(translations.device_health.applications_section_title)} />
              {isLoadingProcessedApplications ? (
                <Box className="device-health-loading-section-container">
                  <LoadingContent />
                </Box>
              ) : (
                <Box className="device-health-section-container">
                  {processedApplications && processedApplications.length > 0 ? (
                    processedApplications.map((application) => {
                      return (
                        <InfoBox
                          label={application.applicationName}
                          value={`${t(translations.device_health.version_label)} ${
                            application.applicationVersion
                          }`}
                          avatar={{
                            type: 'other',
                            url: application.applicationPictureUrl,
                          }}
                        />
                      );
                    })
                  ) : (
                    <Typography variant="body1" component="div">
                      {t(translations.device_health.no_data)}
                    </Typography>
                  )}
                </Box>
              )}
              {/* ----- Versions ----- */}
              <SectionTitle title={t(translations.device_health.versions_section_title)} />
              <Box className="device-health-section-container">
                {processedVersions ? (
                  <>
                    <InfoBox
                      label={t(translations.device_health.agent_label)}
                      value={processedVersions.agent}
                    />
                    <InfoBox
                      label={t(translations.device_health.electron_label)}
                      value={processedVersions.electron}
                    />
                    <InfoBox
                      label={t(translations.device_health.launcher_binary_label)}
                      value={processedVersions.launcherBinary}
                    />
                    <InfoBox
                      label={t(translations.device_health.launcher_frontend_label)}
                      value={processedVersions.launcherFrontend}
                    />
                    <InfoBox
                      label={t(translations.device_health.chrome_label)}
                      value={processedVersions.chrome}
                    />
                  </>
                ) : (
                  <Typography variant="body1" component="div">
                    {t(translations.device_health.no_data)}
                  </Typography>
                )}
              </Box>

              {/* ----- Cache ----- */}
              <SectionTitle title={t(translations.device_health.cache_images_section_title)} />
              <Box className="device-health-section-container">
                <InfoBox
                  label={t(translations.device_health.size_label)}
                  value={
                    deviceHealth.cache?.images?.limitCacheSize
                      ? `${deviceHealth.cache.images.limitCacheSize.toFixed(2)} ${t(
                          translations.device_health.mb_unit_label
                        )}`
                      : '-'
                  }
                />
                <InfoBox
                  label={t(translations.device_health.used_disks_label)}
                  value={
                    deviceHealth.cache?.images?.currentCacheSize
                      ? `${deviceHealth.cache.images.currentCacheSize.toFixed(2)} ${t(
                          translations.device_health.mb_unit_label
                        )}`
                      : ''
                  }
                />
                <InfoBox
                  label={t(translations.device_health.creation_date_label)}
                  value={
                    deviceHealth.cache?.images?.creationDate
                      ? moment(deviceHealth.cache.images.creationDate).format('LL (LT)')
                      : '-'
                  }
                />
                <InfoBox
                  label={t(translations.device_health.expiration_date_label)}
                  value={
                    deviceHealth.cache?.images?.expirationDate
                      ? moment(deviceHealth.cache.images.expirationDate).format('LL (LT)')
                      : '-'
                  }
                />
              </Box>

              {/* ----- Tracking ----- */}
              {props.caregiverMode === CaregiverMode.SystemAdministrator && (
                <>
                  <SectionTitle
                    title={t(translations.device_health.request_tracking_section_title)}
                  />
                  <Box className="device-health-section-container">
                    <InfoBox
                      label={t(translations.device_health.country_label)}
                      value={
                        processedTrackingData?.country && processedTrackingData.countryCode
                          ? `${processedTrackingData.country} (${processedTrackingData.countryCode})`
                          : undefined
                      }
                    />
                    <InfoBox
                      label={t(translations.device_health.city_label)}
                      value={processedTrackingData?.city}
                    />
                    <InfoBox
                      label={t(translations.device_health.city_longitude_label)}
                      value={processedTrackingData?.cityLongitude?.toString()}
                    />
                    <InfoBox
                      label={t(translations.device_health.city_latitude_label)}
                      value={processedTrackingData?.cityLatitude?.toString()}
                    />
                    <InfoBox
                      label={t(translations.device_health.region_label)}
                      value={processedTrackingData?.region}
                    />
                    <InfoBox
                      label={t(translations.device_health.language_label)}
                      value={processedTrackingData?.language}
                    />
                    <InfoBox
                      label={t(translations.device_health.user_ip_address_label)}
                      value={processedTrackingData?.userIP}
                    />
                  </Box>
                  <ComposableMap
                    projection="geoMercator"
                    projectionConfig={{
                      scale: 400,
                      center:
                        processedTrackingData?.cityLatitude && processedTrackingData?.cityLongitude
                          ? [
                              processedTrackingData.cityLongitude,
                              processedTrackingData.cityLatitude,
                            ]
                          : [15.2551, 54.526], // Center in Europe if no data exist
                    }}
                    height={400}
                    style={{backgroundColor: '#F8F9FA'}}
                  >
                    <Geographies geography="/world-map.json">
                      {({geographies}) =>
                        geographies.map((geo) => {
                          // Find country
                          const isCountry: boolean =
                            geo.properties['Alpha-2'] &&
                            (geo.properties['Alpha-2'] as string).toLocaleLowerCase() ===
                              processedTrackingData?.countryCode?.toLocaleLowerCase()
                              ? true
                              : false;

                          return (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              fill={isCountry ? theme.palette.primary.light : '#DADCE0'}
                              stroke="#FFFFFF"
                              strokeWidth={1}
                              style={{
                                default: {
                                  outline: 'none',
                                },
                                hover: {
                                  outline: 'none',
                                },
                                pressed: {
                                  outline: 'none',
                                },
                              }}
                            />
                          );
                        })
                      }
                    </Geographies>
                    {processedTrackingData?.cityLatitude &&
                      processedTrackingData?.cityLongitude &&
                      processedTrackingData?.city && (
                        <Annotation
                          subject={[
                            processedTrackingData.cityLongitude,
                            processedTrackingData.cityLatitude,
                          ]}
                          connectorProps={{
                            stroke: theme.palette.primary.main,
                            strokeWidth: 2,
                            strokeLinecap: 'round',
                          }}
                          dx={-50}
                          dy={-40}
                        >
                          <text
                            x={-8}
                            fontSize="smaller"
                            textAnchor="end"
                            alignmentBaseline="middle"
                            fill={theme.palette.primary.main}
                          >
                            {processedTrackingData.city}
                          </text>
                        </Annotation>
                      )}
                  </ComposableMap>
                </>
              )}
            </Box>
          ) : (
            <Typography variant="body2" component="p" className="device-health-no-data-text">
              {t(translations.device_health.no_data)}
            </Typography>
          )}
        </ContentPage>
      )}
    </>
  );
};
